import { patchOrgPlaybooksEditSessionsByEditSessionIdMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import type { GraphOperation } from '@onefootprint/request-types/dashboard';
import { useMutation } from '@tanstack/react-query';
import { createEditSessionStore } from '@/hooks/use-in-progress-edit-session-storage';
import useStore from './use-store';

const useEdit = () => {
  const store = useStore();
  const mutation = useMutation({
    ...patchOrgPlaybooksEditSessionsByEditSessionIdMutation({}),
    mutationKey: ['patchOrgPlaybooksEditSessionsByEditSessionId'],
  });
  const showRequestErrorToast = useRequestErrorToast();

  const mutateAsync = async (operation: GraphOperation) => {
    try {
      const editSession = await store.getOrCreateEditSession();
      const response = await mutation.mutateAsync({
        path: { editSessionId: editSession.id },
        body: { expectedLastGraphId: editSession.graphId, operation },
      });

      store.setGraphUpdate(response);
      // Only an empty form's errors wait for its save. A paste answers with `add_node` too, and a kind with
      // no form (hasConfiguration false) arrives configured: their errors are real, and nothing could save them.
      const added = response.operationResult;
      if (operation.kind === 'add_node' && added?.kind === 'add_node' && added.hasConfiguration) {
        store.markNodePristine(added.rootNodeId);
      }
      if (operation.kind === 'update_node') {
        store.markNodeTouched(operation.nodeId);
      }
      // Save session ID using playbookId from response
      // Zustand's persist middleware handles localStorage by key name, so creating a new store instance is fine
      const storageStore = createEditSessionStore(response.playbookId);
      storageStore.getState().update(response.startVersionId, response.id);
      return response.operationResult;
    } catch (error) {
      showRequestErrorToast(error);
      throw error;
    }
  };

  return {
    ...mutation,
    mutateAsync,
  };
};

export default useEdit;
